import { get, post } from 'aws-amplify/api';
import { systemInfo } from '../libs/utils';
import { trimObjFields } from '../libs/utils-ts';
import { RateLimiter } from 'limiter';
import config from '../config';

// API Limit is 10 calls per 10s
const limiter = new RateLimiter({ tokensPerInterval: 10, interval: 10000 });
const API_NAME = 'authdiag-backend-support-dev';
const ADMIN_API_NAME = 'authdiag-backend-support-admin';
const PATH_PREFIX = '/supportui/';

const APIWrap = {
  // ...API,
  /**
   * Make a GET request
   * @param {string} apiName  - The api name of the request
   * @param {string} path - The path of the request
   * @param {json} [init] - Request extra params
   * @return {Promise} - A promise that resolves to an object with response status and JSON data, if successful.
   */
  get: async (apiName, path, init) => {
    await limiter.removeTokens(1);
    const { body } =  await get({ 
      apiName, 
      path,
      options: {
        headers: init.headers,
      }
    }).response;
    return await body.json();
  },
  /**
   * Make a POST request
   * @param {string} apiName  - The api name of the request
   * @param {string} path - The path of the request
   * @param {json} [init] - Request extra params
   * @return {Promise} - A promise that resolves to an object with response status and JSON data, if successful.
   */
  post: async (apiName, path, init) => {
    const postHeaders = { 'Content-Type': 'application/json' };

    await limiter.removeTokens(1);
    const { body } = await post({
      apiName, 
      path, 
      options: {
        body: init.body,
        response: init.response,
        headers: init.headers
      }
    }).response;
    return await body.json();
  },
};

/**
 * Make am API Request
 * @param {string} method - HTTP method of request (GET, POST)
 * @param {json} body - The body of the request
 * @param {json} pathSuffix - The suffix of the route's path (ex. For "/supportui/getUserSummary", pathSuffix is "getUserSummary")
 * @return {json} - JSON data of the API request, if successful
 */
const apiReq = async (method, body, pathSuffix, admin, response=undefined) => {
  const apiFunc = method === 'GET' ? APIWrap.get : APIWrap.post;

  return await apiFunc(admin === "ADMIN" ? ADMIN_API_NAME : API_NAME, `${PATH_PREFIX}${pathSuffix}`, {
    body: body,
    response: response,
    headers: {
      Authorization: window.localStorage.getItem('Id-Token'),
    },
  })
    .then((result) => {
      return result;
    })
    .catch(async (error) => {
      if (error.response && [401, 403].indexOf(error.response.statusCode) !== -1) {
        const result = await requestToken(
          window.localStorage.getItem('Refresh-Token'),
          true
        );
        if (result) {
          document.dispatchEvent(
            new CustomEvent('unauthUser', { bubbles: true })
          );
        } else {
          return await apiFunc(API_NAME, `${PATH_PREFIX}${pathSuffix}`, {
            body: body,
            response: response,
            headers: {
              Authorization: window.localStorage.getItem('Id-Token'),
            },
          })
            .then((result) => {
              return result;
            })
            .catch((error) => {
              if (
                error.response &&
                [401, 403].indexOf(error.response.status) !== -1
              ) {
                document.dispatchEvent(
                  new CustomEvent('unauthUser', { bubbles: true })
                );
              } else {
                if (error.response) {
                  if (error.response?.data?.errorDesc) {
                    return { error: error.response.data.errorDesc };
                  }
                  return { error: error.response };
                } else {
                  return { error: error };
                }
              }
            });
        }
      } else if (error.response) {
        console.log("error : ", error);
        if (error.response.data?.errorDesc) {
          return { error: error.response.data.errorDesc };
        }
        return { error: error.response };
      } else {
        console.log("error 2: ", error);
        return { error: error };
      }
    });
};

const SUPPORT_ADMIN_CREATE_USER = 1;

export async function requestToken(code, refresh) {
  const redirectStr =
    config.AUTH_DIAG_ENVIRONMENT === 'DEV'
      ? 'http://localhost:3000/Home'
      : 'https://' + window.location.hostname + '/Home';

  console.log("redirectStr: ", redirectStr);
  console.log("config: ", config);
  const grantType = refresh ? 'refresh_token' : 'authorization_code';
  let bodyStr =
    'grant_type=' +
    grantType +
    '&' +
    'client_id=' +
    config.cognito.APP_CLIENT_ID;
  if (refresh) {
    bodyStr += '&refresh_token=' + code;
  } else {
    bodyStr +=
      '&code=' + code + '&redirect_uri=' + redirectStr + '&scope=openid';
  }

  return await fetch(config.cognito.DOMAIN + '/oauth2/token', {
    method: 'post',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Content-Length': bodyStr.length,
    },
    body: bodyStr,
  })
    .then((result) => {
      return result.json();
    })
    .then((response) => {
      if (response.message) {
        return response.message;
      } else {
        localStorage.setItem('Access-Token', response.access_token);
        localStorage.setItem('Id-Token', response.id_token);
        if (!refresh) {
          localStorage.setItem('Refresh-Token', response.refresh_token);
        }
        const currentTimeInMilliseconds = new Date().getTime();
        const expireTime =
          response.expires_in * 1000 + currentTimeInMilliseconds;
        localStorage.setItem('Expire-Time', expireTime);
        return null;
      }
    })
    .catch((err) => {
      console.log('err: ', err);
      return err;
    });
}

export async function createSupportUser(
  userName,
  password,
  firstName,
  lastName,
  email,
  userType,
  authProvider
) {
  let body = {
    userName: userName,
    password: password || 'Password1234!',
    firstName: firstName,
    lastName: lastName,
    email: email,
    userType: userType,
    authProvider: authProvider,
    functionID: SUPPORT_ADMIN_CREATE_USER,
  };

  if (!password) {
    delete body.password;
  }

  //   cache = [];
  return await apiReq('POST', body, 'execSupportAdminFunction');
}

export async function verifyEmail(code, username, clientId, email, language) {
  return await fetch(
    config.apiGateway.URL +
      '/supportui/verifyUserEmail?code=' +
      code +
      '&username=' +
      username +
      '&clientId=' +
      clientId +
      '&email=' +
      email,
    { response: true }
  )
    .then((result) => {
      return result.json();
    })
    .then((response) => {
      if (response?.errorDesc) {
        return { error: response.errorDesc };
      } else {
        return { message: response.message };
      }
    })
    .catch((err) => {
      return err;
    });
}
export async function getUserRecord() {
  return await apiReq('GET', {}, 'getSupportUserInfo', null, true);
}

export async function updateSupportUser(firstName, lastName, email) {
  let body = {
    firstName: firstName,
    lastName: lastName,
    email: email,
  };

  //   cache = [];
  return await apiReq('POST', body, 'updateSupportUserInfo', "ADMIN");
}

export async function updateSupportUserState(userID, userState) {
  let body = {
    userID: userID,
    userState: userState,
  };

  //   cache = [];
  return await apiReq('POST', body, 'updateSupportUserState', "ADMIN");
}

export async function updateSupportUserType(userID, userType) {
  let body = {
    userID: userID,
    userType: userType,
  };

  //   cache = [];
  return await apiReq('POST', body, 'updateSupportUserType', "ADMIN");
}

export async function changeSupportUserPassword(accessToken, oldPassword, newPassword) {
  let body = {
    AccessToken: accessToken,
    oldPassword: oldPassword,
    newPassword: newPassword,
  };

  return await apiReq('POST', body, 'changeSupportUserPassword');
}

export async function getSupportUserList() {
  return await apiReq('GET', {}, 'getSupportUserList', 'ADMIN');
}

export async function getUserSummary(queryType, queryValue) {
  if (queryType === 'EMAIL') queryValue = queryValue.toLowerCase();
  let body = {
    queryType: queryType.split(' ').join(''),
    queryValue: queryValue,
  };

  return await apiReq('POST', body, 'getUserSummary');
}

export async function getUserSummaryByProperty(queryType, queryValue) {
  let body = {
    queryType: queryType,
    queryValue: queryValue,
  };

  return await apiReq('POST', body, 'getUserSummaryByProperty');
}

export async function getShopSummary(shopID) {
  let body = {
    shopID: shopID,
  };

  return await apiReq('POST', body, 'getShopSummary');
}

export async function getCrmStats() {
  let body = {};

  return await apiReq('POST', body, 'getCrmStats', "ADMIN");
}

export async function getShopNames(shopIDs) {
  let body = {
    shopIDs: shopIDs,
  };

  return await apiReq('POST', body, 'getShopNames');
}

export async function getUserNames(userIDs) {
  let body = {
    userIDs: userIDs,
  };

  return await apiReq('POST', body, 'getUserNames');
}

export async function getShops(shopIDs) {
  let body = {
    shopIDs: shopIDs,
  };

  return await apiReq('POST', body, 'getShops');
}

export async function getShopOrders(shopID) {
  let first = true;
  let allOrders = [];

  let body = {
    shopID: shopID,
    ExclusiveStartKey: null,
  };

  while (body.ExclusiveStartKey || first) {
    const response = await apiReq('POST', body, 'getShopOrders');
    body.ExclusiveStartKey = response.LastEvaluatedKey;
    allOrders = [...allOrders, ...response.orders];
    first = false;
  }

  return allOrders;
}

export async function getShopServices(shopID) {
  let first = true;
  let allServices = [];

  let body = {
    shopID: shopID,
    ExclusiveStartKey: null,
  };

  while (body.ExclusiveStartKey || first) {
    const response = await apiReq('POST', body, 'getShopServices');
    body.ExclusiveStartKey = response.LastEvaluatedKey;
    allServices = [...allServices, ...response.services];
    first = false;
  }

  return allServices;
}

export async function getShopParts(shopID) {
  let first = true;
  let allParts = [];

  let body = {
    shopID: shopID,
    ExclusiveStartKey: null,
  };

  while (body.ExclusiveStartKey || first) {
    const response = await apiReq('POST', body, 'getShopParts');
    body.ExclusiveStartKey = response.LastEvaluatedKey;
    allParts = [...allParts, ...response.parts];
    first = false;
  }

  return allParts;
}

export async function getShopCustomers(shopID) {
  let first = true;
  let allCustomers = [];

  let body = {
    shopID: shopID,
    ExclusiveStartKey: null,
  };

  while (body.ExclusiveStartKey || first) {
    const response = await apiReq('POST', body, 'getShopCustomers');
    body.ExclusiveStartKey = response.LastEvaluatedKey;
    allCustomers = [...allCustomers, ...response.customers];
    first = false;
  }

  return allCustomers;
}

export async function getShopVehicles(shopID) {
  let first = true;
  let allVehicles = [];

  let body = {
    shopID: shopID,
    ExclusiveStartKey: null,
  };

  while (body.ExclusiveStartKey || first) {
    const response = await apiReq('POST', body, 'getShopVehicles');
    body.ExclusiveStartKey = response.LastEvaluatedKey;
    allVehicles = [...allVehicles, ...response.vehicles];
    first = false;
  }

  return allVehicles;
}

export async function getShopEvents(shopID) {
  let first = true;
  let allEvents = [];

  let body = {
    shopID: shopID,
    ExclusiveStartKey: null,
  };

  while (body.ExclusiveStartKey || first) {
    const response = await apiReq('POST', body, 'getShopEvents');
    body.ExclusiveStartKey = response.LastEvaluatedKey;
    allEvents = [...allEvents, ...response.events];
    first = false;
  }

  return allEvents;
}

export async function getAllShopNames() {
  let body = {};

  let results = await apiReq('POST', body, 'getAllShopNames');
  let shops = results.shops;
  while (results.lastEvaluatedKey) {
    body.lastEvaluatedKey = results.lastEvaluatedKey;
    results = await apiReq('POST', body, 'getAllShopNames');
    shops = shops.concat(results.shops);
  }
  return shops;
}

export async function getShopSummaryByProperty(queryType, queryValue) {
  let body = {
    queryType: queryType,
    queryValue: queryValue,
  };

  let results = await apiReq('POST', body, 'getShopSummaryByProperty');
  let shopList = results.shopList;
  while (results.lastEvaluatedKey) {
    body.lastEvaluatedKey = results.lastEvaluatedKey;
    results = await apiReq('POST', body, 'getShopSummaryByProperty');
    shopList = shopList.concat(results.shopList);
  }
  return shopList;
}

export async function getUserLog(userID, startDate, endDate) {
  let body = {
    userID: userID,
    startDate: startDate,
    endDate: endDate,
  };

  let results = await apiReq('POST', body, 'getUserLog');
  let logs = results.logEntries;
  while (results.LastEvaluatedKey) {
    body.LastEvaluatedKey = results.LastEvaluatedKey;
    results = await apiReq('POST', body, 'getUserLog');
    logs = logs.concat(results.logEntries);
  }
  return logs;
}

export async function getShopLog(shopID, startDate, endDate) {
  const body = {
    shopID: shopID,
    startDate: startDate,
    endDate: endDate,
  };

  let LastEvaluatedKey = true; // = true So we can enter the while loop
  let error;
  let logEntries = [];

  while (LastEvaluatedKey && !error) {
    //If LastEvaluatedKey exists and it is not our true value, it will run again
    // because we have a legit LastEvaluatedKey
    if (LastEvaluatedKey !== true) {
      body.LastEvaluatedKey = LastEvaluatedKey;
    }
    const response = await apiReq('POST', body, 'getShopInternalAuditLog');
    error = response.error;
    if (!error) {
      logEntries = logEntries.concat(response.logEntries);
      LastEvaluatedKey = response.LastEvaluatedKey;
    }
  }

  LastEvaluatedKey = true; // to enter the second while loop

  while (LastEvaluatedKey && !error) {
    //If LastEvaluatedKey exists and it is not our true value, it will run again
    // because we have a legit LastEvaluatedKey
    if (LastEvaluatedKey !== true) {
      body.LastEvaluatedKey = LastEvaluatedKey;
    }
    const response = await apiReq('POST', body, 'getShopAuditLog');
    error = response.error;
    if (!error) {
      logEntries = logEntries.concat(response.logEntries);
      LastEvaluatedKey = response.LastEvaluatedKey;
    }
  }

  if (error) {
    return { error };
  } else {
    return { logEntries };
  }
}

export async function getToolLog(
  queryType,
  queryValue,
  shopID,
  startDate,
  endDate
) {
  let body = {
    queryType: queryType,
    queryValue: queryValue,
    shopID: shopID,
    startDate: startDate,
    endDate: endDate,
  };

  let result = await apiReq('POST', body, 'getToolLog');

  if (result.error) return result;

  let filteredEntries = result.logEntries.filter(
    (entry) => entry.actionCode !== 'SUPPORT_GET_TOOL_LOG'
  );
  return {
    logEntries: filteredEntries,
  };
}

export async function getEmailLog(queryType, queryValue, startDate, endDate) {
  if (queryType === 'EMAIL') queryValue = queryValue.toLowerCase();
  let body = {
    queryType: queryType,
    queryValue: queryValue,
    startDate: startDate,
    endDate: endDate,
  };

  return await apiReq('POST', body, 'getEmailLog');
}

export async function getLoginActivityLog(queryValue, startDate, endDate) {
  let body = {
    userName: queryValue,
    startDate: startDate || null,
    endDate: endDate || null,
  };
  return await apiReq('POST', body, 'getLoginActivityForUsername');
}

export async function getSupportAuditLog(userID, startDate, endDate) {
  let body = {
    userID: userID,
    startDate: startDate,
    endDate: endDate,
  };

  return await apiReq('POST', body, 'getSupportAuditLog');
}

export async function removeToolFromShop(shopID, toolID) {
  let body = {
    shopID: shopID,
    toolID: toolID,
  };

  //   cache = [];
  return await apiReq('POST', body, 'removeToolFromShop');
}

export async function getToolRecoveryRequests(showClosed, startDate, endDate) {
  const body = {
    showClosed,
    startDate,
    endDate,
  };
  return await apiReq('POST', body, 'getToolRecoveryRequests');
}

export async function getToolRecoveryImage(docKey) {
  let body = {
    docKey: docKey,
  };

  return await apiReq('POST', body, 'getToolRecoveryImageURL')
    .then(async (response) => {
      console.log(response);
      return await fetch(response);
    })
    .then(async (response) => {
      const blob = await response.blob();
      return blob;
    })
    .catch((error) => {
      if (error.response) {
        return { error: error.response.data.errorDesc };
      } else if (error?.errorDesc) {
        return { error: error.errorDesc };
      } else if (error?.message) {
        return { error: error.message };
      } else {
        return { error: error.toString() };
      }
    });
}

export async function getToolTaxExemptFileURL(docKey) {
  let body = {
    docKey: docKey,
  };

  return await apiReq('POST', body, 'getToolRecoveryImageURL')
    .then(async (response) => {
      return response;
    })
    .catch((error) => {
      if (error.response) {
        return { error: error.response.data.errorDesc };
      } else if (error?.errorDesc) {
        return { error: error.errorDesc };
      } else if (error?.message) {
        return { error: error.message };
      } else {
        return { error: error.toString() };
      }
    });
}

export async function contactToolRecoveryOwner(toolID) {
  let body = {
    toolID: toolID,
  };

  //   cache = [];
  return await apiReq('POST', body, 'contactToolRecoveryOwner');
}

export async function processToolRecoveryRequest(toolID, status, reason) {
  let body = {
    toolID: toolID,
    status: status,
    reason: reason,
  };

  //   cache = [];
  return await apiReq('POST', body, 'processToolRecoveryRequest');
}

export async function closeToolRecoveryRequest(toolID, reason) {
  let body = {
    toolID: toolID,
    reason: reason,
  };

  //   cache = [];
  return await apiReq('POST', body, 'closeToolRecoveryRequest');
}

export async function refundTransaction(
  transactionID,
  shopID,
  amount,
  taxOnly,
  refundDetails
) {
  let body = {
    transactionID: transactionID,
    shopID: shopID,
    amount: amount,
    taxOnly: taxOnly,
    refundDetails: refundDetails,
  };

  //   cache = [];
  return await apiReq('POST', body, 'refundTransaction');
}

export async function terminateShop(shopID) {
  let body = {
    shopID: shopID,
  };

  //   cache = [];
  return await apiReq('POST', body, 'terminateShop');
}

export async function downgradeShop(shopID) {
  let body = {
    shopID: shopID,
  };

  //   cache = [];
  return await apiReq('POST', body, 'downgradeShop', "ADMIN");
}

export async function inactivateShop(shopID) {
  let body = {
    shopID: shopID,
  };

  return await apiReq('POST', body, 'inactivateShop', "ADMIN");
}

export async function removeOEMFromShop(shopID, oemID) {
  let body = {
    shopID: shopID,
    oemID: oemID,
  };

  //   cache = [];
  return await apiReq('POST', body, 'removeOEMFromShop', "ADMIN");
}

export async function updateShopState(shopID, state) {
  let body = {
    shopID: shopID,
    state: state,
  };

  //   cache = [];
  return await apiReq('POST', body, 'updateShopState');
}

export async function getExtendedToolInfo(query, queryType) {
  let body = {
    query: query,
    queryType: queryType,
  };

  return await apiReq('POST', body, 'getExtendedToolInfo');
}

export async function getVinInfo(vins) {
  let body = {
    vins: vins,
  };

  return await apiReq('POST', body, 'getVinInfo');
}

export async function getSystemConfig() {
  return await apiReq('GET', {}, 'getUIConfigs');
}

export async function recordSupportUserSession() {
  let sysInfo = systemInfo();
  let body = {
    browserName: sysInfo.browserName,
    browserVersion: String(sysInfo.browserVersion),
    hostOS: sysInfo.hostOS,
  };

  let res = await apiReq('POST', body, 'recordSupportUserSession');
  if (res.error) {
    console.log('Error trying to record user session: ', res.error);
  }
}

export async function sendBulkEmail(
  usersType,
  emailAddress,
  emailSubject,
  emailBodyHtml,
  emailBodyText
) {
  let body = {
    usersType,
    emailAddress,
    emailBodyHtml,
    emailBodyText,
    emailSubject,
  };

  return await apiReq('POST', body, 'sendBulkEmail', "ADMIN");
}

export async function getOEMRegionList() {
  return await apiReq('GET', {}, 'getOEMRegionList');
}

export async function addBanner(messageBanner, pathBanner, severityBanner) {
  let body = {
    messageBanner,
    pathBanner,
    severity: severityBanner,
  };
  return await apiReq('POST', body, 'addBanner', "ADMIN");
}

export async function getBanner() {
  return await apiReq('GET', {}, 'getBanner', "ADMIN");
}

export async function getBannerActivity(startDate, endDate) {
  const body = {
    startDate,
    endDate,
  };
  return await apiReq('POST', body, 'getBannerActivity', "ADMIN");
}

export async function recordAcceptedTrustXPI(userID, processInstanceId) {
  let body = {
    userID,
    processInstanceId,
  };

  //   cache = [];
  return await apiReq('POST', body, 'recordAcceptedTrustXPI');
}

export const removeTechCertsFromShop = async (shopID, numToRemove) => {
  let body = {
    shopID,
    numToRemove,
  };
  return await apiReq('POST', body, 'removeTechCertsFromShop', "ADMIN");
};

export const getAvalaraExemptions = async () => {
  return await apiReq('GET', {}, 'getAvalaraExemptions', 'ADMIN');
};

export const getAvalaraZones = async () => {
  return await apiReq('GET', {}, 'getAvalaraZones', 'ADMIN');
};

export const markShopTaxExempt = async (shopID, taxpayerIdNumber, taxCert, taxCertEffectiveDate, exemptionReason, exposureZone) => {
  let body = {
    shopID,
    taxpayerIdNumber,
    taxCert,
    taxCertEffectiveDate,
    exemptionReason,
    exposureZone,
  };

  return await apiReq('POST', body, 'markShopTaxExempt', "ADMIN");
};

export const getOemHealth = async () => {
  return await apiReq('POST', {}, 'getOemHealth');
}

export async function getVINLog(
  vin,
  shopID,
  startDate,
  endDate
) {
  let body = {
    vin: vin,
    shopID: shopID,
    startDate: startDate,
    endDate: endDate,
  };

  let result = await apiReq('POST', body, 'getVinLog');

  if (result.error) return result;

  let filteredEntries = result.logEntries?.filter(
    (entry) => entry.actionCode !== 'SUPPORT_GET_VIN_LOG'
  );
  return {
    logEntries: filteredEntries,
  };
}

export const removeUsersFromShop = async (shopID, numToRemove) => {
  let body = {
    shopID,
    numToRemove,
  };
  return await apiReq('POST', body, 'removeUsersFromShop', "ADMIN");
};

export const generateMfaQrCode = async (username) => {
  let body = {
    username,
  };
  return await apiReq('POST', body, 'generateMfaQrCode');
};

export const verifyMfaQrCode = async (username, totp) => {
  let body = {
    username,
    totp
  };
  return await apiReq('POST', body, 'verifyMfaQrCode');
};

export const resetSupportUserMfaEnrollment = async (username, email) => {
  let body = {
    username,
    email
  };
  return await apiReq('POST', body, 'resetSupportUserMfaEnrollment');
};

export const getIsValidResetMfaEnrollmentCode = async (resetMfaEnrollmentCode) => {
  let body = {
    resetMfaEnrollmentCode,
  };
  return await apiReq('POST', body, 'getIsValidResetMfaEnrollmentCode');
};


export const finishResetSupportUserMfaEnrollment = async (resetMfaEnrollmentCode, totp) => {
  let body = {
    resetMfaEnrollmentCode,
    totp,
  };
  return await apiReq('POST', body, 'finishResetSupportUserMfaEnrollment');
};

export const transferDisputeInfo = async (toolID) => {
  let body = {
    toolID,
  };
  return await apiReq('POST', body, 'transferDisputeInfo', "ADMIN");
};

export const verifyShopUserId = async (shopID, userID, shopUserIdInfo) => {
  let body = {
    shopID,
    userID,
    shopUserIdInfo,
  };
  return await apiReq('POST', body, 'verifyShopUserId', "ADMIN");
};

export const getEmailVerifiedStatus = async (userName) => {
  let body = {
    userName
  };
  return await apiReq('POST', body, 'checkEmailVerificationStatus');
};

export const setEmailAddressVerified = async (userName) => {
  let body = {
    userName
  };
  return await apiReq('POST', body, 'setEmailAddressVerified');
};

// Tax Exempt Requests
export const getTaxExemptRequests = async (requestStatus, startDate, endDate) => {
  let body = {};
  // Only include status if provided (null means return all statuses)
  if (requestStatus) {
    body.status = requestStatus;
  }
  // If one date parameter is provided, both are required
  if (startDate && endDate) {
    body.startDate = startDate;
    body.endDate = endDate;
  }
  return await apiReq('POST', body, 'getTaxExemptRequests');
}

export async function approveTaxExemptRequest(teRequestID, userID, exemptionReason, reasonID, zendeskTicketID, skipRefunds) {
  let body = {
    teRequestID,
    userID,
    processType: 'APPROVE',
    exemptionReason,
    reasonID,
    zendeskTicketID,
    skipRefunds,
  };
  return await apiReq('POST', body, 'processTaxExemptRequest');
}

export async function rejectTaxExemptRequest(teRequestID, userID, rejectionReason, zendeskTicketID) {
  let body = {
    teRequestID,
    userID,
    processType: 'REJECT',
    rejectionReason,
    zendeskTicketID
  };
  return await apiReq('POST', body, 'processTaxExemptRequest');
}

export const getBadToolSerials = async (includeDeleted) => {
  let LastEvaluatedKey = null;
  let badToolSerials = [];

  while (true) {
    const body = { includeDeleted, LastEvaluatedKey };

    const response = await apiReq('POST', body, 'getBadToolSerials').then((result) => {
      if (result?.error) return result;
      else return result;
    });

    if (response?.error) {
      return response;
    }

    badToolSerials = [...badToolSerials, ...(response?.badToolSerials ?? [])];

    LastEvaluatedKey = response?.LastEvaluatedKey;

    if (!LastEvaluatedKey) break;
  }

  return badToolSerials;
}

export const createBadToolSerial = async (toolManufacturerUUID, toolSerial, message, toolModelUUID) => {
  const body = {
    toolManufacturerUUID,
    toolSerial,
    message,
  };

  if (toolModelUUID) {
    body.toolModelUUID = toolModelUUID;
  }

  return await apiReq('POST', body, 'createBadToolSerial');
}

export const updateBadToolSerial = async (badToolSerialUUID, toolManufacturerUUID, toolSerial, message, toolModelUUID) => {
  const body = {
    badToolSerialUUID,
    toolManufacturerUUID,
    toolSerial,
    message,
  };

  if (toolModelUUID) {
    body.toolModelUUID = toolModelUUID;
  }

  return await apiReq('POST', body, 'updateBadToolSerial');
}

export const removeBadToolSerial = async (badToolSerialUUID) => {
  const body = { badToolSerialUUID };

  return await apiReq('POST', body, 'removeBadToolSerial');
}

export const getToolTypes = async () => {
  const body = {};

  return await apiReq('POST', body, 'getToolManufacturerList');
}